import config from "environment";
import { getRequest, postRequest } from "./client";
import {
  PolicyAdjustment,
  PolicyProject,
  PolicyProjectType,
  LinkRecord,
} from "winslow-core";

const { baseApi } = config;

export async function apiGetPolicyProjects(
  userId: string
): Promise<PolicyProject[] | null> {
  const fetchURL = `${baseApi}/policy-studio/${userId}`;
  return await getRequest(fetchURL, "API/apiGetPolicyProjects");
}

export async function apiUpdatePolicyProject(payload: {
  id: string;
  title?: string;
  content?: string;
  type?: PolicyProject["type"];
}): Promise<PolicyProject | null> {
  const fetchURL = `${baseApi}/policy-studio/update`;
  return await postRequest(fetchURL, payload, "API/apiUpdatePolicyProject");
}

export async function apiCreatePolicyProject(
  userId: string,
  type: PolicyProjectType
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/create`;
  return await postRequest(
    fetchURL,
    { userId, type },
    "API/apiCreatePolicyProject"
  );
}

export async function apiUpdatePolicyProjectFromAdjustment(
  policyId: string,
  adjustment: PolicyAdjustment
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/update-from-adjustment`;
  return await postRequest(
    fetchURL,
    { policyId, adjustment },
    "API/apiUpdatePolicyProjectFromAdjustment"
  );
}

export async function apiUpdatePolicyProjectFromPrompt(
  policyId: string,
  prompt: string
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/update-from-prompt`;
  return await postRequest(
    fetchURL,
    { policyId, prompt },
    "API/apiUpdatePolicyProjectFromPrompt"
  );
}

export async function apiCreatePolicyProjectFromLinkRecord(
  userId: string,
  linkRecordId: string,
  type: PolicyProjectType
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/create-from-link-record`;
  return await postRequest(
    fetchURL,
    { userId, linkRecordId, type },
    "API/apiCreatePolicyProjectFromLinkRecord"
  );
}

export async function apiGetUserLinkRecords(
  workspaceId: string,
  userId: string
): Promise<LinkRecord[] | null> {
  const fetchURL = `${baseApi}/policy-studio/link-records/${workspaceId}/${userId}`;
  return await getRequest(fetchURL, "API/apiGetUserLinkRecords");
}

export async function apiEmbedPolicyProjectAsResource(
  policyProjectId: string,
  replaceExisting: boolean,
  linkRecordId: string | null
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/embed-as-resource`;
  return await postRequest(
    fetchURL,
    { policyProjectId, replaceExisting, linkRecordId },
    "API/apiEmbedPolicyProjectAsResource"
  );
}

export async function apiCheckConflicts(
  policyProjectId: string,
  linkRecordIds: string[]
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/check-conflicts`;
  return await postRequest(
    fetchURL,
    { policyProjectId, linkRecordIds },
    "API/apiCheckConflicts"
  );
}

export async function apiCheckCompliance(
  policyProjectId: string,
  jurisdictionContent: string
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/check-compliance`;
  return await postRequest(
    fetchURL,
    { policyProjectId, jurisdictionContent },
    "API/apiCheckCompliance"
  );
}

export async function apiCheckPublicCompliance(
  policyProject: PolicyProject,
  jurisdictionContent: string,
  email: string | null
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/public/check-compliance`;
  return await postRequest(
    fetchURL,
    { policyProject, jurisdictionContent, email },
    "API/apiCheckPublicCompliance"
  );
}

export async function apiGetContentTokenCount(
  text: string
): Promise<number | null> {
  const fetchURL = `${baseApi}/policy-studio/get-content-token-count`;
  return await postRequest(fetchURL, { text }, "API/apiGetContentTokenCount");
}

export async function apiGetEmbeddingContentTokenCount(
  linkRecordId: string
): Promise<number | null> {
  const fetchURL = `${baseApi}/policy-studio/get-embedding-content-token-count/${linkRecordId}`;
  return await getRequest(fetchURL, "API/apiGetEmbeddingContentTokenCount");
}

export async function apiDeletePolicyProject(
  policyProjectId: string
): Promise<boolean> {
  const fetchURL = `${baseApi}/policy-studio/delete`;
  return await postRequest(
    fetchURL,
    { policyProjectId },
    "API/apiDeletePolicyProject"
  );
}

export async function apiCreatePublicPolicyProject(
  type: PolicyProjectType,
  email?: string
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/public/create`;
  return await postRequest(
    fetchURL,
    { type, email },
    "API/apiCreatePublicPolicyProject"
  );
}

export async function apiUpdatePublicPolicyProjectFromAdjustment(
  policyProject: PolicyProject,
  adjustment: PolicyAdjustment,
  email: string | null
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/public/update-from-adjustment`;
  return await postRequest(
    fetchURL,
    { policyProject, adjustment, email },
    "API/apiUpdatePublicPolicyProjectFromAdjustment"
  );
}

export async function apiUpdatePublicPolicyProjectFromPrompt(
  policyProject: PolicyProject,
  prompt: string,
  email: string | null
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/public/update-from-prompt`;
  return await postRequest(
    fetchURL,
    { policyProject, prompt, email },
    "API/apiUpdatePublicPolicyProjectFromPrompt"
  );
}

export async function apiUpdatePublicPolicyProjectContent(
  policyProject: PolicyProject,
  content: string,
  email: string | null
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/public/update-content`;
  return await postRequest(
    fetchURL,
    { policyProject, content, email },
    "API/apiUpdatePublicPolicyProjectContent"
  );
}

export async function apiUpdatePolicyProjectContent(
  id: string,
  content: string
): Promise<PolicyProject> {
  const fetchURL = `${baseApi}/policy-studio/update-content`;
  return await postRequest(
    fetchURL,
    { id, content },
    "API/apiUpdatePolicyProjectContent"
  );
}
